/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import ActivityLogList from "./components/ActivityLogList";
import ActivityLogSearch from "./components/ActivityLogSearch";
import ActivityLogToolbar from "./components/ActivityLogToolbar";
import moment from "moment";

interface Filters {
  [key: string]: any;
}

const initFrom = moment().subtract(1, "month");
const initTo = moment();
const initialDateRangeFilter = [initFrom.toDate(), initTo.toDate()];

const rowPerPage = 10;

const initialFilters: Filters = {
  from: initFrom,
  to: initTo,
  merchant_id: "",
  suba_id: "",
  role: "",
  action: "",
  username: "",
  details: "",
};

interface PayloadType {
  [key: string]: any;
}

const ActivityLog = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedDates, setSelectedDates] = useState<Date[]>(
    initialDateRangeFilter
  );
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);


  // NEW: control filter toggle
  const [showFilters, setShowFilters] = useState(false);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      localStorage.removeItem("merchantIdToFilterActivityLogs");
      localStorage.removeItem("subaccountIdToFilterActivityLogs");
      setIsLoading(true);
      await fetchSystemLogs({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        ...filters,
      }, false, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      setIsLoading(false);
      setIsFilterLoading(false);
    };

    fetchData();
  }, []);


  const fetchSystemLogs = async (
    payload: PayloadType = {},
    isReset = false,
    signal?: AbortSignal,
    requestId?: number
  ) => {
    try {
      const formattedFromDate = moment(
        !isReset ? selectedDates[0] : initFrom,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 00:00");
      const formattedToDate = moment(
        !isReset ? selectedDates[1] : initTo,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 23:59");

      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        from: formattedFromDate,
        to: formattedToDate,
      };

      const merchantId = localStorage.getItem("merchantIdToFilterActivityLogs");
      const subaccountId = localStorage.getItem(
        "subaccountIdToFilterActivityLogs"
      );

      if (merchantId && merchantId !== "" && merchantId !== undefined) {
        queryObject.merchant_id = merchantId;
      }

      if (subaccountId && subaccountId !== "" && subaccountId !== undefined) {
        delete queryObject.merchant_id;
        queryObject.suba_id = subaccountId;
      }

      if (payload?.role !== "" && payload?.role !== undefined) {
        queryObject.role = payload?.role;
      }

      if (payload?.username !== "" && payload?.username !== undefined) {
        queryObject.username = payload?.username;
      }

      if (payload?.action !== "" && payload?.action !== undefined) {
        queryObject.action = payload?.action;
      }

      if (payload?.details !== "" && payload?.details !== undefined) {
        queryObject.details = payload?.details;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/admin/systemlog?${queryString}`,
        { signal }
      );

      // Don't update state if request was aborted
      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.systemlogs?.payload;

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      let newLastKey = response.data.systemlogs?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = async (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters,
    };

    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    await fetchSystemLogs(newFilters);
    setIsLoading(false);
  };

  // const handleClickCreate = () => {
  //   navigate(`/${userRole}/account-management/create`);
  // };

  const handleSearchSubmit = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    setCurrentLastKey("");
    setLastKeys({});
    setPage(0);
    await fetchSystemLogs({ page: 0, limit: rowPerPage, lastkey: "", ...filters });
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  const handleSearchClear = () => {
    handleResetTable();
  };

  const handleResetTable = async () => {
    localStorage.removeItem("merchantIdToFilterActivityLogs");
    localStorage.removeItem("subaccountIdToFilterActivityLogs");
    setIsLoading(true);
    setIsFilterLoading(true);
    setFilters(initialFilters);
    setLastKeys([]);
    setCurrentLastKey("");
    setPage(0);
    setSelectedDates(initialDateRangeFilter);
    await fetchSystemLogs(
      {
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        ...initialFilters,
      },
      true
    );
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  const triggerReloadActivityLogList = async () => {
    setIsLoading(true);
    await fetchSystemLogs({
      page: 0,
      limit: rowPerPage,
      lastkey: "",
      ...initialFilters,
    });
    setIsLoading(false);
  };

  return (
    <>
      <ActivityLogToolbar
        isLoading={isLoading || isFilterLoading}
        onToggleFilters={() => setShowFilters((p) => !p)}
        onRefresh={triggerReloadActivityLogList}
      />

      <ActivityLogSearch
        showFilters={showFilters}
        isLoading={isLoading}
        filters={filters}
        setFilters={setFilters}
        setSelectedDates={setSelectedDates}
        selectedDates={selectedDates}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
        isFilterLoading={isFilterLoading}
      />
      <ActivityLogList
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        tableData={isLoading ? [] : tableData}
        setPaginationModel={setPaginationModel}
        currentLastKey={currentLastKey}
      />
    </>
  );
};

export default ActivityLog;
